import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Calendar as AntCalendar, Button, Flex, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { PLAN_TYPE_VALUES } from 'constants/profile';
import { APPOINTMENT_STATUSES } from 'constants/appointments';
import { useNavigate } from 'react-router-dom';
import { EventClickArg } from '@fullcalendar/core';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import { Content } from './styled';

const CALENDAR_STATUSES: number[] = [
  APPOINTMENT_STATUSES.approved,
  APPOINTMENT_STATUSES.started
];

const Calendar: FC = () => {
  const { t } = useTranslation();
  const calendarRef = useRef<FullCalendar | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState<LegacyAppointment[]>([]);

  useEffect(() => {
    fetchAppointments({
      plan_type: `${PLAN_TYPE_VALUES.video_call}`,
      page: 1,
      limit: 999
    })
      .then(res => setAppointments(res.results))
      .catch(() => setAppointments([]));
  }, []);

  const results = appointments
    .filter(
      item =>
        CALENDAR_STATUSES.includes(item.status) &&
        item.dates.some(date => date.accepted)
    )
    .map(item => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const startDate = item.dates.find(date => date.accepted)!;

      const duration = item.plan.duration || 1;

      const end = dayjs(startDate.date).add(duration, 'hours').format();

      const title = t('calendar.appointment_with', {
        name: `${item.patient.first_name} ${item.patient.last_name}`
      });

      return {
        id: String(item.id),
        start: startDate.date,
        end,
        title: title,
        editable: false
      };
    });

  useEffect(() => {
    calendarRef.current?.getApi().gotoDate(date.toDate());
  }, [date]);

  const title = useMemo(() => {
    if (date.isSame(dayjs(), 'day')) {
      return t('calendar.schedule_list', {
        day: t('calendar.today')
      });
    }

    return t('calendar.schedule_list', {
      day: `${date.format('DD.MM.YYYY')},`
    });
  }, [date, t]);

  const onEventClick = (e: EventClickArg) => {
    if (e.event.id) {
      return navigate(`/appointments/details/${e.event.id}`);
    }
  };

  return (
    <Content>
      <AntCalendar
        fullscreen={false}
        value={date}
        onChange={setDate}
        headerRender={({ value, onChange }) => {
          const current = value.clone();

          return (
            <Flex
              className="agenda-calendar-header"
              justify="space-between"
              align="center"
            >
              <Space size={0}>
                <Button
                  type="link"
                  onClick={() => onChange(current.subtract(1, 'year'))}
                >
                  <span className="ant-picker-super-prev-icon" />
                </Button>
                <Button
                  type="link"
                  onClick={() => onChange(current.subtract(1, 'month'))}
                >
                  <span className="ant-picker-prev-icon" />
                </Button>
              </Space>
              <p className="agenda-calendar-title">
                {value.format('MMM YYYY')}
              </p>
              <Space size={0}>
                <Button
                  type="link"
                  onClick={() => onChange(current.add(1, 'month'))}
                >
                  <span className="ant-picker-next-icon" />
                </Button>
                <Button
                  type="link"
                  onClick={() => onChange(current.add(1, 'year'))}
                >
                  <span className="ant-picker-super-next-icon" />
                </Button>
              </Space>
            </Flex>
          );
        }}
      />
      <Typography.Title level={4} className="agenda-title">
        {title}
      </Typography.Title>
      <FullCalendar
        ref={calendarRef}
        plugins={[timeGridPlugin, dayGridPlugin]}
        eventClick={onEventClick}
        initialView="timeGridDay"
        events={results}
        height={360}
        nowIndicator
        scrollTime={dayjs().subtract(2, 'hours').format('HH:mm:ss')}
        allDaySlot={false}
        eventAllow={e => e.start.getDay() === e.end.getDay()}
        eventClassNames="agenda-event-item"
        headerToolbar={{
          left: '',
          right: ''
        }}
        slotLabelFormat={{
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
          meridiem: false
        }}
      />
    </Content>
  );
};

export default memo(Calendar);
